import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Typography from '../Text/Typography';
import Colors from '../Colors/Colors';
import Icon from '../Icon/Icon';
import { useAuth } from '../../contexts/AuthContext';
import { authenticatedFetch } from '../../config/api';
import { API_BASE_URL } from '../../config/api';
import { Routine } from '../../types/routine';
import './Sidebar.css';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStatus, user } = useAuth();
  const [customRoutines, setCustomRoutines] = useState<Routine[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isCustomRoutineActive = (routineType: string) => location.pathname === `/routines/custom/${routineType}`;

  // Memoize the user ID to prevent unnecessary re-renders
  const userId = useMemo(() => user?.id, [user?.id]);

  // Fetch custom routines - memoized to prevent infinite loops
  const fetchCustomRoutines = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${userId}/custom`);
      if (response.ok) {
        const routines = await response.json();
        setCustomRoutines(routines);
      }
    } catch (error) {
      console.error('Error fetching custom routines:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Fetch custom routines when user ID changes
  useEffect(() => {
    // User is guaranteed to be available by AuthGuard
    fetchCustomRoutines();
  }, [userId, fetchCustomRoutines]);

  // Listen for custom routine changes - only add listener once
  useEffect(() => {
    const handleRoutineChange = () => {
      fetchCustomRoutines();
    };

    window.addEventListener('customRoutineChanged', handleRoutineChange);
    return () => {
      window.removeEventListener('customRoutineChanged', handleRoutineChange);
    };
  }, [fetchCustomRoutines]);

  return (
    <aside className="sidebar">
      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <ul className="sidebar-menu">
            <li className={`sidebar-item ${isActive('/') ? 'active' : ''}`}>
              <button
                type="button"
                className="sidebar-link"
                onClick={() => navigate('/')}
              >
                <Colors variant="black">
                  <Typography variant="body-bold">
                    Home
                  </Typography>
                </Colors>
              </button>
            </li>
            <li className={`sidebar-item ${isActive('/skincarecabinet') ? 'active' : ''}`}>
              <button
                type="button"
                className="sidebar-link"
                onClick={() => navigate('/skincarecabinet')}
              >
                <Colors variant="black">
                  <Typography variant="body-bold">
                    Skincare Cabinet
                  </Typography>
                </Colors>
              </button>
            </li>
          </ul>
        </div>
        
        <div className="sidebar-section">
          <Colors variant="dark-gray">
            <Typography variant="body-small-bold" className="sidebar-header">
              Routines
            </Typography>
          </Colors>
          <ul className="sidebar-menu">
            <li className={`sidebar-item ${isActive('/routines/morning') ? 'active' : ''}`}>
              <button
                type="button"
                className="sidebar-link"
                onClick={() => navigate('/routines/morning')}
              >
                <Colors variant="black">
                  <Typography variant="body-bold">
                    Morning Routine
                  </Typography>
                </Colors>
              </button>
            </li>
            <li className={`sidebar-item ${isActive('/routines/night') ? 'active' : ''}`}>
              <button
                type="button"
                className="sidebar-link"
                onClick={() => navigate('/routines/night')}
              >
                <Colors variant="black">
                  <Typography variant="body-bold">
                    Night Routine
                  </Typography>
                </Colors>
              </button>
            </li>
            {/* Custom Routines */}
            {customRoutines.map((routine) => (
              <li key={routine.id} className={`sidebar-item ${isCustomRoutineActive(routine.routine_type) ? 'active' : ''}`}>
                <button
                  type="button"
                  className="sidebar-link"
                  onClick={() => navigate(`/routines/custom/${routine.routine_type}`)}
                >
                  <Colors variant="black">
                    <Typography variant="body-bold">
                      {routine.routine_name}
                    </Typography>
                  </Colors>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="sidebar-section">
          <Colors variant="dark-gray">
            <Typography variant="body-small-bold" className="sidebar-header">
              Learn More
            </Typography>
          </Colors>
          <ul className="sidebar-menu">
            <li className={`sidebar-item ${isActive('/learn/about') ? 'active' : ''}`}>
              <button
                type="button"
                className="sidebar-link"
                onClick={() => navigate('/learn/about')}
              >
                <Colors variant="black">
                  <Typography variant="body-bold">
                    About
                  </Typography>
                </Colors>
              </button>
            </li>
            <li className={`sidebar-item ${isActive('/help') ? 'active' : ''}`}>
              <button
                type="button"
                className="sidebar-link"
                onClick={() => navigate('/help')}
              >
                <Colors variant="black">
                  <Typography variant="body-bold">
                    Contact
                  </Typography>
                </Colors>
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;